/**
 * The all-entities screen's controller — OND-52.
 *
 * ONE FETCH, AND IT IS ONE THE PRODUCT ALREADY MAKES. The entities come out of
 * `useAccount`, which is the single copy of who is signed in; the holdings come
 * out of `GET /v1/collections`, which already spans every entity the caller
 * administers. There is no entities endpoint and there is deliberately none —
 * see the header of `entities.model.ts`.
 */

import { useCallback, useEffect, useState } from "react";

import type { CollectionSummary, EntityOrgType } from "@ondo/shared";

import { useAccount } from "../lib/account.ts";
import { fetchCollections } from "../lib/api/collections.ts";
import { fetchMe } from "../lib/api/me.ts";
import { createEntity } from "../lib/api/organisations.ts";
import { describeApiError } from "../lib/apiError.ts";
import { ENTITIES_COPY, createdMessage } from "./entities.constants.ts";
import { resolveEntities, validateEntityName } from "./entities.model.ts";
import type { EntitiesView } from "./entities.model.ts";

export interface EntitiesController {
  view: EntitiesView;
  /** The create form's own state, which is independent of the list's. */
  form: {
    name: string;
    orgType: EntityOrgType;
    busy: boolean;
    error: string | null;
    /** What the last successful creation said, cleared when the form is reused. */
    created: string | null;
  };
  setName: (name: string) => void;
  setOrgType: (orgType: EntityOrgType) => void;
  submitEntity: () => void;
  /** Counts an entity in the totals, or takes it out. */
  toggleEntity: (ondoId: string) => void;
}

export function useEntitiesController(): EntitiesController {
  const { me: meState, replaceMe } = useAccount();
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // THE SET OF ENTITIES TURNED OFF, not the set turned on — see `toEntityRows`.
  // An entity created while this screen is open is counted immediately, which
  // is the only behaviour that reads as "it worked".
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());

  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState<EntityOrgType>("company");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetchCollections()
      .then((rows) => {
        if (active) setCollections(rows);
      })
      .catch((cause: unknown) => {
        if (active) setFailure(describeApiError(cause, ENTITIES_COPY.holdingsEmpty));
      });

    return () => {
      active = false;
    };
  }, [nonce]);

  const toggleEntity = useCallback((ondoId: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (!next.delete(ondoId)) next.add(ondoId);
      return next;
    });
  }, []);

  const submitEntity = useCallback(() => {
    const invalid = validateEntityName(name);
    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setBusy(true);
    setError(null);
    setCreated(null);

    createEntity({ displayName: name.trim(), orgType })
      // THE ACCOUNT IS REREAD, NOT PATCHED. The new seat is a membership, and
      // `/me` is the one place anything reads memberships from — a client that
      // appended a row would be deciding what a seat means, which is the
      // server's answer and nobody else's.
      .then(async (entity) => {
        replaceMe(await fetchMe());
        setName("");
        setCreated(createdMessage(entity.displayName, entity.ondoId));
        setNonce((value) => value + 1);
      })
      .catch((cause: unknown) => {
        setError(describeApiError(cause, ENTITIES_COPY.createFailed));
      })
      .finally(() => {
        setBusy(false);
      });
  }, [name, orgType, replaceMe]);

  const view: EntitiesView =
    failure !== null
      ? { kind: "error", message: failure }
      : meState.status !== "ready" || collections === null
        ? { kind: "loading" }
        : resolveEntities({ me: meState.me, collections, hidden });

  return {
    view,
    form: { name, orgType, busy, error, created },
    setName: useCallback((next: string) => {
      setName(next);
      setError(null);
      setCreated(null);
    }, []),
    setOrgType,
    submitEntity,
    toggleEntity,
  };
}
